import { useState, useCallback } from 'react';
import { Loader2, CheckCircle, Clock, XCircle, AlertCircle, ExternalLink, RefreshCw, Globe, Upload, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog } from '../ui';
import { useApiEffect } from '../../hooks';
import type { PortalConfig } from '../../types';
import { formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

// Publicação por veículo/portal, conforme o backend (GET /veiculos/{id}/publicacoes)
interface Publicacao {
  id: number;
  portal: string;
  status: string;            // PUBLICADO, NAO_PUBLICADO, AGUARDANDO, EM_REVISAO, DESATUALIZADO, ERRO, REMOVIDO, XML
  idExterno?: string;
  urlAnuncio?: string;
  dataPublicacao?: string;
  erroMensagem?: string;
}

const portalLabels: Record<string, string> = {
  OLX: 'OLX',
  MERCADOLIVRE: 'Mercado Livre',
  MERCADO_LIVRE: 'Mercado Livre',
  WEBMOTORS: 'WebMotors',
  ICARROS: 'iCarros',
  MOBIAUTO: 'Mobiauto',
  SITE: 'Site Próprio',
};

// Identifica o tipo de portal a partir do valor armazenado (tolera MERCADO_LIVRE / MERCADOLIVRE)
type PortalKind = 'OLX' | 'MERCADOLIVRE' | 'WEBMOTORS' | 'OUTRO';
const portalKind = (portal: string): PortalKind => {
  const p = portal.toUpperCase().replace(/_/g, '');
  if (p === 'OLX') return 'OLX';
  if (p === 'MERCADOLIVRE') return 'MERCADOLIVRE';
  if (p === 'WEBMOTORS') return 'WEBMOTORS';
  return 'OUTRO';
};

// Status do backend → rótulo + cores + ícone
const statusInfo = (status: string): { label: string; classes: string; Icon: typeof CheckCircle } => {
  switch (status?.toUpperCase()) {
    case 'PUBLICADO':
    case 'ATIVO':
      return { label: 'Publicado', classes: 'bg-green-100 text-green-700 border-green-200', Icon: CheckCircle };
    case 'AGUARDANDO':
    case 'EM_REVISAO':
    case 'PENDENTE':
      return { label: 'Em revisão', classes: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Clock };
    case 'DESATUALIZADO':
      return { label: 'Desatualizado', classes: 'bg-yellow-100 text-yellow-700 border-yellow-200', Icon: AlertCircle };
    case 'ERRO':
      return { label: 'Erro', classes: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle };
    case 'REMOVIDO':
    case 'EXPIRADO':
      return { label: 'Removido', classes: 'bg-gray-100 text-gray-500 border-gray-200', Icon: AlertCircle };
    case 'XML':
      return { label: 'Via XML Feed', classes: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Globe };
    default:
      return { label: 'Não publicado', classes: 'bg-gray-50 text-gray-500 border-gray-200', Icon: Globe };
  }
};

export function PublicacoesVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showApiError } = useNotification();

  const [publicacoes, setPublicacoes] = useState<Publicacao[]>([]);
  const [portaisConfig, setPortaisConfig] = useState<PortalConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingPortal, setDeletingPortal] = useState<string | null>(null);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);
    try {
      const [pubsRes, configRes] = await Promise.all([
        api.get<Publicacao[]>(`/veiculos/${veiculoId}/publicacoes`, { signal }),
        api.get<PortalConfig[]>('/portais', { signal }),
      ]);
      setPublicacoes(pubsRes.data);
      setPortaisConfig(configRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const labelOf = (portal: string) => portalLabels[portal.toUpperCase()] || portalLabels[portal.toUpperCase().replace(/_/g, '')] || portal;

  // Publica (ou republica) o veículo no portal, usando o cliente correto de cada plataforma.
  const handlePublicar = async (portal: string) => {
    const kind = portalKind(portal);
    setActionLoading(portal);
    try {
      if (kind === 'OLX') {
        const res = await api.post(`/integracoes/olx/veiculos/${veiculoId}/publicar`);
        showSuccess(res.data?.mensagem || 'Anúncio enviado à OLX.');
      } else if (kind === 'WEBMOTORS') {
        // Webmotors (Estoque de Canais): o estoque vem do Cockpit; aqui sincronizamos status/link.
        await api.post('/integracoes/webmotors/sincronizar-estoque');
        showSuccess('Estoque Webmotors sincronizado.');
      } else {
        // Mercado Livre e demais portais via fluxo genérico (garante a linha de publicação antes).
        await api.post(`/veiculos/${veiculoId}/publicacoes`, { portal }).catch(() => undefined);
        await api.post(`/veiculos/${veiculoId}/publicacoes/${portal}/publicar`);
        showSuccess(`Veículo publicado no ${labelOf(portal)}`);
      }
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setActionLoading(null);
    }
  };

  // Sincroniza o status/link do anúncio a partir da plataforma.
  const handleSincronizar = async (portal: string) => {
    const kind = portalKind(portal);
    setActionLoading(portal);
    try {
      if (kind === 'OLX') {
        await api.post('/integracoes/olx/sincronizar-status');
      } else if (kind === 'MERCADOLIVRE') {
        await api.post('/integracoes/sincronizacao/MERCADOLIVRE/importar');
      } else if (kind === 'WEBMOTORS') {
        await api.post('/integracoes/webmotors/sincronizar-estoque');
      } else {
        await api.post(`/portais/publicacoes/${veiculoId}/${portal}/sincronizar`);
      }
      showSuccess(`Status sincronizado com ${labelOf(portal)}`);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemover = async () => {
    if (!deletingPortal) return;
    setActionLoading(deletingPortal);
    try {
      await api.delete(`/veiculos/${veiculoId}/publicacoes/${deletingPortal}`);
      showSuccess(`Publicação removida do ${labelOf(deletingPortal)}`);
      setShowDeleteDialog(false);
      setDeletingPortal(null);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setActionLoading(null);
    }
  };

  const activePortais = portaisConfig.filter((p) => p.ativo);
  const inactivePortais = portaisConfig.filter((p) => !p.ativo);
  const getPublicacao = (portal: string) => publicacoes.find((p) => p.portal.toUpperCase() === portal.toUpperCase());

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Publicações - ${veiculoNome}`} size="lg">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Portais Configurados</h3>
              {activePortais.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nenhum portal configurado. Configure os portais em Integrações → Plataformas.</p>
              ) : (
                activePortais.map((config) => {
                  const pub = getPublicacao(config.portal);
                  const info = statusInfo(pub?.status || '');
                  const kind = portalKind(config.portal);
                  const loading = actionLoading === config.portal;
                  const publicado = (pub?.status || '').toUpperCase() === 'PUBLICADO';

                  return (
                    <div key={config.portal} className={`p-4 border rounded-lg ${info.classes}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <info.Icon className="w-5 h-5 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">{labelOf(config.portal)}</p>
                            <div className="flex items-center gap-2 text-sm flex-wrap">
                              <span className={`px-1.5 py-0.5 text-xs rounded border ${info.classes}`}>{info.label}</span>
                              {pub?.dataPublicacao && (
                                <span className="text-gray-500">Publicado em {formatDate(pub.dataPublicacao)}</span>
                              )}
                              {pub?.idExterno && <span className="text-gray-400 text-xs">#{pub.idExterno}</span>}
                            </div>
                            {pub?.erroMensagem && <p className="text-sm text-red-600 mt-1 break-words">{pub.erroMensagem}</p>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {pub?.urlAnuncio && (
                            <a
                              href={pub.urlAnuncio}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                              title="Ver anúncio na plataforma"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}

                          {kind === 'WEBMOTORS' ? (
                            <button
                              onClick={() => handleSincronizar(config.portal)}
                              disabled={loading}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                              title="A Webmotors recebe o estoque pelo Cockpit; aqui sincronizamos status/link"
                            >
                              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                              Sincronizar estoque
                            </button>
                          ) : (
                            <>
                              {publicado && (
                                <button
                                  onClick={() => handleSincronizar(config.portal)}
                                  disabled={loading}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                                >
                                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                  Sincronizar
                                </button>
                              )}
                              <button
                                onClick={() => handlePublicar(config.portal)}
                                disabled={loading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
                              >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {publicado ? 'Republicar' : 'Publicar'}
                              </button>
                            </>
                          )}

                          {pub && (
                            <button
                              onClick={() => {
                                setDeletingPortal(config.portal);
                                setShowDeleteDialog(true);
                              }}
                              disabled={loading}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg disabled:opacity-50"
                              title="Remover publicação"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {inactivePortais.length > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <h3 className="font-medium text-gray-500">Portais Não Configurados</h3>
                <div className="grid grid-cols-2 gap-2">
                  {inactivePortais.map((config) => (
                    <div key={config.portal} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-gray-500">
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">{labelOf(config.portal)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{publicacoes.filter((p) => (p.status || '').toUpperCase() === 'PUBLICADO').length} publicação(ões) ativa(s)</span>
                <button onClick={() => refetch()} className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                  <RefreshCw className="w-4 h-4" />
                  Atualizar
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingPortal(null);
        }}
        onConfirm={handleRemover}
        type="danger"
        title="Remover Publicação"
        message={`Tem certeza que deseja remover a publicação do ${deletingPortal ? labelOf(deletingPortal) : ''}? O anúncio será despublicado do portal.`}
        confirmText="Remover"
      />
    </Modal>
  );
}
